import type {
  BookPerformanceRow,
  CommissionStatementReportRow,
  CommissionStatementRowRecord,
} from '@nadoutong/contracts';
import { commissionPeriodWindow } from '@nadoutong/contracts';
import { useMemo, useState } from 'react';
import {
  useBookPerformance,
  useCommissionStatementDetail,
  useCommissionStatements,
} from '../api/commissionReports.ts';
import { OpsApiError } from '../api/client.ts';
import {
  formatCnyCents,
  formatLocalDate,
  formatUsdMicros,
  recentPeriodKeys,
} from '../components/formatters.ts';
import { CustomerCell } from '../components/identity/CustomerCell.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import {
  Chip,
  EmptyState,
  KpiCard,
  PageHeader,
  Panel,
  Select,
  TBody,
  Table,
  TableEmpty,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * /crm/commission — commission statements + lot-based book performance
 * (epic #1034 E5-M5, #1147).
 *
 * ATTRIBUTION LAYERING (the load-bearing invariant, mirrored in the copy):
 * the commission block reads `commission_statements` (RECEIPT attribution —
 * owner stamped at the money event's verification); the book block reads
 * credit-lot owner stamps (LOT attribution — per-customer consumption).
 * The two are NEVER summed and are labeled apart so they cannot read as
 * reconciling.
 *
 * Scope is server-derived from the session role (ops-api): a CM sees only
 * their own statements + own book; a lead sees their brand; admin/finance
 * see cross-brand + the house line. The console never sends a scope param.
 */

function errorText(error: unknown): string {
  if (error instanceof OpsApiError) return `${error.code}: ${error.message}`;
  return error instanceof Error ? error.message : String(error);
}

export function CommissionReportPage(): JSX.Element {
  const t = useT();
  const { status } = useSession();
  const periods = useMemo(() => recentPeriodKeys(6), []);
  const [period, setPeriod] = useState<string>(() => periods[0] ?? '');
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);

  const enabled = status === 'authenticated';
  const window = useMemo(() => commissionPeriodWindow(period), [period]);

  const statements = useCommissionStatements({ brand: null, period }, enabled);
  const book = useBookPerformance(
    { brand: null, from: window?.startIso ?? '', to: window?.endIso ?? '' },
    enabled && window !== null,
  );
  const detail = useCommissionStatementDetail(selectedStatementId, null, enabled);

  const statementRows = statements.data?.statements ?? [];
  const bookRows = book.data?.rows ?? [];
  // Displayed total = stored + Σ post-lock adjustments (the figure finance
  // settled), never the frozen stored total alone.
  const commissionTotalUsdMicros = statementRows.reduce(
    (sum, s) => sum + s.displayed_total_usd_micros,
    0,
  );
  const bookMarginCnyCents = bookRows.reduce((sum, r) => sum + r.margin_cny_cents, 0);

  return (
    <section className="flex flex-col gap-8" data-testid="commission-report-page">
      <PageHeader
        title={t('commission_report.title')}
        sub={t('commission_report.sub')}
        actions={
          <Select
            value={period}
            onValueChange={setPeriod}
            options={periods.map((p) => ({ value: p, label: p }))}
            ariaLabel={t('commission_report.period_aria')}
            mono
            testId="commission-report-period-select"
          />
        }
      />

      {/* The two KPIs are deliberately unlabeled as a sum: one is
          receipt-attributed money owed, the other lot-attributed margin. */}
      <div className="grid gap-3 md:grid-cols-2">
        <KpiCard
          label={t('commission_report.kpi.commission')}
          value={formatUsdMicros(commissionTotalUsdMicros, commissionTotalUsdMicros < 0)}
          testId="commission-report-kpi-commission"
        />
        <KpiCard
          label={t('commission_report.kpi.book_margin')}
          value={formatCnyCents(bookMarginCnyCents, bookMarginCnyCents < 0)}
          testId="commission-report-kpi-book-margin"
        />
      </div>

      <CommissionSection
        loading={statements.isLoading}
        error={statements.isError ? errorText(statements.error) : null}
        rows={statementRows}
        selectedId={selectedStatementId}
        onSelect={(id) => setSelectedStatementId((prev) => (prev === id ? null : id))}
      />

      {selectedStatementId !== null ? (
        <StatementDetailPanel
          loading={detail.isLoading}
          error={detail.isError ? errorText(detail.error) : null}
          rows={detail.data?.rows ?? []}
        />
      ) : null}

      <BookSection
        loading={book.isLoading}
        error={book.isError ? errorText(book.error) : null}
        rows={bookRows}
      />
    </section>
  );
}

// ─── commission (RECEIPT attribution) ────────────────────────────────────────

function CommissionSection({
  loading,
  error,
  rows,
  selectedId,
  onSelect,
}: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly rows: readonly CommissionStatementReportRow[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={
        <span className="flex items-baseline gap-2">
          <span>{t('commission_report.commission.title')}</span>
          <span className="text-dense font-normal text-paper-low">
            {t('commission_report.commission.attribution')}
          </span>
        </span>
      }
      count={rows.length}
      testId="commission-report-commission"
    >
      {loading ? <TableSkeleton rows={4} /> : null}
      {error !== null ? (
        <EmptyState title={t('commission_report.error.load')} hint={error} testId="commission-report-commission-error" />
      ) : null}
      {!loading && error === null ? (
        <Table>
          <THead>
            <Tr>
              <Th>{t('commission_report.commission.table.period')}</Th>
              <Th>{t('commission_report.commission.table.brand')}</Th>
              <Th>{t('commission_report.commission.table.status')}</Th>
              <Th num>{t('commission_report.commission.table.total')}</Th>
              <Th>{t('commission_report.commission.table.updated')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.length === 0 ? (
              <TableEmpty colSpan={5} testId="commission-report-commission-empty">
                {t('commission_report.commission.empty')}
              </TableEmpty>
            ) : (
              rows.map((row) => (
                <Tr
                  key={row.statement.id}
                  interactive
                  onClick={() => onSelect(row.statement.id)}
                  data-testid={`commission-report-statement-${row.statement.id}`}
                  data-selected={row.statement.id === selectedId ? 'true' : undefined}
                >
                  <Td mono>{row.statement.period_start}</Td>
                  <Td mono>{row.statement.brand}</Td>
                  <Td>
                    <Chip tone={row.statement.status === 'confirmed' ? 'ok' : 'neutral'} lamp={false}>
                      {t(`commission_report.status.${row.statement.status}`)}
                    </Chip>
                  </Td>
                  <Td num emphasis>
                    {formatUsdMicros(
                      row.displayed_total_usd_micros,
                      row.displayed_total_usd_micros < 0,
                    )}
                  </Td>
                  <Td>{formatLocalDate(row.statement.updated_at)}</Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      ) : null}
    </Panel>
  );
}

function StatementDetailPanel({
  loading,
  error,
  rows,
}: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly rows: readonly CommissionStatementRowRecord[];
}): JSX.Element {
  const t = useT();
  return (
    <Panel caption={t('commission_report.detail.title')} count={rows.length} testId="commission-report-detail">
      {loading ? <TableSkeleton rows={3} /> : null}
      {error !== null ? (
        <EmptyState title={t('commission_report.error.load')} hint={error} testId="commission-report-detail-error" />
      ) : null}
      {!loading && error === null ? (
        <Table>
          <THead>
            <Tr>
              <Th>{t('commission_report.detail.table.source_type')}</Th>
              <Th>{t('commission_report.detail.table.source_id')}</Th>
              <Th num>{t('commission_report.detail.table.net')}</Th>
              <Th num>{t('commission_report.detail.table.rate')}</Th>
              <Th num>{t('commission_report.detail.table.commission')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.length === 0 ? (
              <TableEmpty colSpan={5} testId="commission-report-detail-empty">
                {t('commission_report.detail.empty')}
              </TableEmpty>
            ) : (
              rows.map((row) => (
                <Tr key={row.id} data-testid={`commission-report-row-${row.id}`}>
                  <Td>{t(`commission_report.source.${row.source_type}`)}</Td>
                  <Td mono>{row.source_id}</Td>
                  <Td num>{formatUsdMicros(row.net_usd_micros, true)}</Td>
                  <Td num>{`${(row.rate_bps / 100).toFixed(2)}%`}</Td>
                  <Td num emphasis>
                    {formatUsdMicros(row.commission_usd_micros, true)}
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      ) : null}
    </Panel>
  );
}

// ─── book performance (LOT attribution) ─────────────────────────────────────

function BookSection({
  loading,
  error,
  rows,
}: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly rows: readonly BookPerformanceRow[];
}): JSX.Element {
  const t = useT();
  return (
    <Panel
      caption={
        <span className="flex items-baseline gap-2">
          <span>{t('commission_report.book.title')}</span>
          <span className="text-dense font-normal text-paper-low">
            {t('commission_report.book.attribution')}
          </span>
        </span>
      }
      count={rows.length}
      testId="commission-report-book"
    >
      {loading ? <TableSkeleton rows={4} /> : null}
      {error !== null ? (
        <EmptyState title={t('commission_report.error.load')} hint={error} testId="commission-report-book-error" />
      ) : null}
      {!loading && error === null ? (
        <>
          <Table>
            <THead>
              <Tr>
                <Th>{t('commission_report.book.table.customer')}</Th>
                <Th num>{t('commission_report.book.table.revenue')}</Th>
                <Th num>{t('commission_report.book.table.gift_cost')}</Th>
                <Th num>{t('commission_report.book.table.margin')}</Th>
                <Th num>{t('commission_report.book.table.charges')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.length === 0 ? (
                <TableEmpty colSpan={5} testId="commission-report-book-empty">
                  {t('commission_report.book.empty')}
                </TableEmpty>
              ) : (
                rows.map((row) => (
                  <Tr key={`${row.owner_ops_user_id}:${row.account_id}`} data-testid={`commission-report-book-${row.account_id}`}>
                    <Td emphasis>
                      {row.is_house ? (
                        t('commission_report.book.house')
                      ) : (
                        <CustomerCell
                          orgId={row.org_id ?? undefined}
                          accountId={row.org_id === null ? row.account_id : undefined}
                          secondary={<span>{row.org_name ?? row.account_id}</span>}
                        />
                      )}
                    </Td>
                    <Td num>{formatCnyCents(row.revenue_cny_cents, false)}</Td>
                    <Td num>{formatCnyCents(row.gift_sales_cost_cny_cents, false)}</Td>
                    <Td num emphasis>
                      {formatCnyCents(row.margin_cny_cents, row.margin_cny_cents < 0)}
                    </Td>
                    <Td num>{row.charge_count}</Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
          {/* Lower-bound caveat: book revenue/margin is lot-attributed
              consumption and lags when a charge's allocation is still
              pending replay (#1147). Commission is receipt-based, unaffected. */}
          <p className="mt-2 text-micro text-paper-low" data-testid="commission-report-book-lag-caveat">
            {t('commission_report.book.lag_caveat')}
          </p>
        </>
      ) : null}
    </Panel>
  );
}
